import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Currency } from '@/components/ui/currency'
import { FolderOpen, Calendar, MapPin, Home, AlertTriangle, User } from 'lucide-react'
import { useProjects } from '@/hooks'
import { useNavigate } from 'react-router'
import type { Project } from '../../../shared-lib/types'

export default function ProjectsPage() {
  const navigate = useNavigate()

  const { data: projectsData, isLoading, error } = useProjects({
    sortBy: "startDate",
    sortOrder: "desc",
    limit: 20
  })

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'PLANNED':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'CANCELLED':
        return 'bg-red-100 text-red-800 border-red-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Enhanced Header Section */}
      <div className="relative bg-gradient-to-r from-blue-600 via-indigo-700 to-purple-800 overflow-hidden">
        <div className="absolute inset-0 bg-black/10">
          <div className="absolute inset-0 bg-white/5"></div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-white/5 rounded-full"></div>
        <div className="absolute bottom-0 left-0 -mb-32 -ml-32 w-64 h-64 bg-white/5 rounded-full"></div>

        <div className="relative container mx-auto px-6 py-16">
          <div className="max-w-4xl">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm border border-white/20">
                <FolderOpen className="h-8 w-8 text-white" />
              </div>
              <div className="flex items-center space-x-2 text-white/80 text-sm">
                <div className="w-2 h-2 bg-white/60 rounded-full"></div>
                <span>Portfolio</span>
                <div className="w-2 h-2 bg-white/60 rounded-full"></div>
                <span>Proyek</span>
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
              Proyek
            </h1>

            <p className="text-xl text-white/90 mb-8 leading-relaxed max-w-3xl">
              Portfolio proyek pembangunan, kegiatan masyarakat, dan program strategis yang sedang berlangsung maupun telah selesai
            </p>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-4">
              <Button
                className="bg-white/20 hover:bg-white/30 text-white border border-white/30 backdrop-blur-sm font-semibold px-6 py-3 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
                onClick={() => navigate('/')}
              >
                <Home className="h-4 w-4 mr-2" />
                Kembali ke Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-6 py-8 space-y-8">
        {/* Projects Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="animate-pulse border-0 shadow-lg">
                <CardHeader>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="h-3 bg-gray-200 rounded"></div>
                    <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : error ? (
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8 text-center">
              <AlertTriangle className="h-16 w-16 text-orange-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Gagal memuat proyek</h3>
              <p className="text-gray-500">Terjadi kesalahan saat memuat data. Silakan coba lagi nanti.</p>
            </CardContent>
          </Card>
        ) : projectsData?.data && projectsData.data.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {projectsData.data.map((project: Project) => (
              <Card key={project.id} className="group hover:shadow-xl transition-all duration-300 border-0 shadow-lg bg-white/90 backdrop-blur-sm hover:-translate-y-1">
                {(project.featuredImage || project.image) && (
                  <div className="aspect-video bg-gray-100 rounded-t-xl overflow-hidden">
                    <img 
                      src={project.featuredImage || project.image} 
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start gap-2">
                    <CardTitle className="flex items-center gap-2 text-gray-900 font-bold group-hover:text-blue-600 transition-colors">
                      <FolderOpen className="h-5 w-5 text-blue-600" />
                      {project.title}
                    </CardTitle>
                    <Badge className={getStatusColor(project.status)} variant="outline">
                      {project.status === 'PLANNED' ? 'Direncanakan' :
                       project.status === 'IN_PROGRESS' ? 'Berlangsung' :
                       project.status === 'COMPLETED' ? 'Selesai' : project.status}
                    </Badge>
                  </div>
                  <CardDescription className="text-gray-600 text-sm">
                    {project.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-4">
                    {/* Coordinator Info */}
                    {project.coordinatorName && (
                      <div className="flex items-center gap-2 text-sm text-gray-600 bg-blue-50 p-2 rounded-lg">
                        <User className="h-4 w-4 text-blue-500" />
                        <div>
                          <span className="font-medium">{project.coordinatorName}</span>
                          {project.coordinatorPosition && (
                            <span className="text-xs text-gray-500 block">{project.coordinatorPosition}</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Progress bar (if available) */}
                    {project.progress !== undefined && project.progress !== null && (
                      <div>
                        <div className="flex justify-between text-sm text-gray-600 mb-2">
                          <span>Progress</span>
                          <span className="font-medium">{project.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-blue-600 to-indigo-600 h-2 rounded-full transition-all duration-300" 
                            style={{ width: `${project.progress}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Project details */}
                    <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-green-500" />
                        <span>{new Date(project.startDate).toLocaleDateString('id-ID')}</span>
                      </div>
                      {project.location && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-red-500" />
                          <span className="truncate">{project.location}</span>
                        </div>
                      )}
                      {project.endDate && (
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-orange-500" />
                          <span>{new Date(project.endDate).toLocaleDateString('id-ID')}</span>
                        </div>
                      )}
                      {project.budget && (
                        <div className="flex items-center gap-2 text-green-600 font-medium">
                          <Currency amount={project.budget} className="text-green-600 font-medium" />
                        </div>
                      )}
                    </div>

                    {/* Categories */}
                    {((project.categories && project.categories.length > 0) || project.category) && (
                      <div className="flex items-center gap-2 flex-wrap">
                        {project.categories && project.categories.length > 0 ? (
                          project.categories.map((category) => (
                            <Badge key={category.id} variant="outline" className="text-xs border-blue-300 text-blue-600 bg-blue-50">
                              {category.name}
                            </Badge>
                          ))
                        ) : project.category && (
                          <Badge variant="outline" className="text-xs border-gray-300 text-gray-600">
                            {project.category.name}
                          </Badge>
                        )}
                      </div>
                    )}

                    {/* Tags */}
                    {project.tags && project.tags.length > 0 && (
                      <div className="flex items-center gap-2 flex-wrap">
                        {project.tags.slice(0, 3).map((tag) => (
                          <Badge key={tag.id} variant="secondary" className="text-xs bg-gray-100 text-gray-600 border border-gray-300">
                            #{tag.name}
                          </Badge>
                        ))}
                        {project.tags.length > 3 && (
                          <Badge variant="secondary" className="text-xs bg-gray-100 text-gray-500">
                            +{project.tags.length - 3} lagi
                          </Badge>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end pt-2 border-t border-gray-100">
                      <Button 
                        size="sm"
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white border-0 rounded-lg shadow-md hover:shadow-lg transition-all"
                        onClick={() => navigate(`/projects/${project.id}`)}
                      >
                        Lihat Detail
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
            <CardContent className="p-12 text-center">
              <div className="relative">
                <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full flex items-center justify-center">
                  <FolderOpen className="h-12 w-12 text-blue-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Tidak ada proyek ditemukan</h3>
                <p className="text-gray-500 mb-6 max-w-md mx-auto">
                  Belum ada proyek yang dibuat saat ini.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Load More */}
        {projectsData?.data && projectsData.data.length > 0 && (
          <div className="flex justify-center">
            <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-gray-600 mb-4">Menampilkan {projectsData.data.length} proyek</p>
                  <Button 
                    variant="outline"
                    className="rounded-xl border-gray-300 hover:bg-gray-50 px-8 py-3"
                  >
                    Muat Lebih Banyak Proyek
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}
